import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { environment } from '../../environments/environment';

export interface ImportStatus {
  importId: string;
  storeId: string;
  adminId: string;
  status: 'processing' | 'completed' | 'error';
  startTime: string;
  endTime?: string;
  duration?: number;
  totalRows: number;
  processedRows: number;
  successfulImports: number;
  currentStage?: string;
  errors: Array<{
    row: number;
    productName: string;
    error: string;
  }>;
  warnings: Array<{
    row: number;
    productName: string;
    warning: string;
  }>;
  newProducts: string[];
  newCategories: string[];
}

export interface ImportResponse {
  code: number;
  message: string;
  data: {
    importId: string;
    fileName: string;
    fileSize: number;
    storeId: string;
    uploadedAt: string;
    status: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class CsvImportService {
  private socket: Socket | null = null;
  private importStatusSubject = new Subject<ImportStatus>();
  private connectionStatusSubject = new BehaviorSubject<boolean>(false);

  private activeImportId: string | null = null;
  private activeStoreId: string | null = null;

  public importStatus$ = this.importStatusSubject.asObservable();
  public connectionStatus$ = this.connectionStatusSubject.asObservable();

  constructor(private http: HttpClient) {}

  /**
   * Track an in-flight import so the reconnect handler can recover it.
   * Call with the importId right after a successful upload response.
   * Pass null to clear once the import reaches a terminal state.
   */
  setActiveImportId(importId: string | null): void {
    this.activeImportId = importId;
  }

  /**
   * Upload CSV file for import
   */
  uploadCSVFile(storeId: string, file: File): Observable<ImportResponse> {
    const formData = new FormData();
    formData.append('csvFile', file);

    const token = localStorage.getItem('token');
    
    if (!token) {
      console.error('No auth token available for file upload. Please ensure you are logged in.');
      throw new Error('Authentication required');
    }
    
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`
    });

    const uploadUrl = `${environment.baseUrl}products/store/${storeId}/import`;
    console.log('CSV Import: Uploading file to:', uploadUrl);
    console.log('CSV Import: File details:', { name: file.name, size: file.size, type: file.type });

    return this.http.post<ImportResponse>(
      uploadUrl,
      formData,
      { headers }
    );
  }

  /**
   * Connect to Socket.IO for real-time updates
   */
  connectSocket(storeId: string): void {
    const token = localStorage.getItem('token');
    
    if (!token) {
      console.error('No auth token available for socket connection. Please ensure you are logged in.');
      console.debug('Available localStorage keys:', Object.keys(localStorage));
      return;
    }
    
    console.log('CSV Import: Connecting to socket with token for store:', storeId);

    // Disconnect existing connection if any
    this.disconnectSocket();

    this.activeStoreId = storeId;

    // Extract base URL for socket connection (remove /api/v1/ suffix)
    const socketUrl = environment.baseUrl.replace('/api/v1/', '');
    
    this.socket = io(socketUrl, {
      auth: { token }
    });

    this.socket.on('connect', () => {
      console.log('Connected to socket server');
      this.connectionStatusSubject.next(true);
      
      // Join store-specific room
      this.socket?.emit('join-store-room', storeId);
      console.log(`Joined store room: ${storeId}`);

      // On reconnect, recover any in-flight import that may have finished
      // while the socket was down by polling the REST status endpoint.
      if (this.activeImportId) {
        console.log(`CSV Import: Reconnected with active import ${this.activeImportId}, polling REST status`);
        const currentToken = localStorage.getItem('token');
        const headers = new HttpHeaders({ 'Authorization': `Bearer ${currentToken}` });
        this.http
          .get<any>(
            `${environment.baseUrl}products/store/${storeId}/import/${this.activeImportId}/status`,
            { headers }
          )
          .subscribe({
            next: (res: any) => {
              if (res?.response?.data) {
                const status: ImportStatus = res.response.data;
                console.log('CSV Import: Recovered status via REST:', status);
                this.importStatusSubject.next(status);
                if (status.status === 'completed' || status.status === 'error') {
                  this.activeImportId = null;
                }
              }
            },
            error: () => {
              // Silently ignore — the socket event will still arrive if the import is running
            }
          });
      }
    });

    this.socket.on('disconnect', () => {
      console.log('Disconnected from socket server');
      this.connectionStatusSubject.next(false);
    });

    this.socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      this.connectionStatusSubject.next(false);
    });

    // Listen for import status updates
    this.socket.on(`csv-import-status-${storeId}`, (status: ImportStatus) => {
      console.log('Import status update:', status);
      this.importStatusSubject.next(status);
      if (status.status === 'completed' || status.status === 'error') {
        this.activeImportId = null;
      }
    });
  }

  /**
   * Disconnect from Socket.IO
   */
  disconnectSocket(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.connectionStatusSubject.next(false);
    }
  }

  /**
   * Get connection status
   */
  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  /**
   * Validate CSV file
   */
  validateCSVFile(file: File): { valid: boolean; error?: string } {
    // Check file type
    const validTypes = ['.csv', 'text/csv', 'application/vnd.ms-excel'];
    const fileType = file.type;
    const fileExtension = file.name.toLowerCase().split('.').pop();

    if (fileExtension !== 'csv' && !validTypes.includes(fileType)) {
      return { valid: false, error: 'Only CSV files are allowed' };
    }

    // Check file size (10MB max)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      return { valid: false, error: 'File too large. Maximum size is 10MB per file.' };
    }

    return { valid: true };
  }

  /**
   * Download CSV template from backend (always up-to-date with import parser).
   */
  downloadTemplate(storeId: string): void {
    const url = `${environment.baseUrl}products/store/${storeId}/csv-template`;

    this.http.get(url, { responseType: 'blob' }).subscribe({
      next: (blob) => {
        const objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = 'products_template.csv';
        link.click();
        window.URL.revokeObjectURL(objectUrl);
      },
      error: (err) => {
        console.error('CSV template download failed:', err);
      }
    });
  }
}
